import { FC } from 'react'

import {
  BankingPaymentFilter,
  BankingTransferFilter,
  PaymentsOverdueWidgetDocument,
  PaymentsOverdueWidgetQuery,
} from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import {
  ChartData,
  FinanceWidget,
  SupportedCharts,
} from 'modules/finance/components/Widgets/common/FinanceWidget'
import { buildWidget } from 'modules/finance/components/Widgets/common/widgets'

interface PreparedDataArgsType {
  amountFormatter: (value: number) => string
  displayedFilters: (string | string[])[]
}

interface QueryArgsType {
  currency: string
  paymentsFilters?: BankingPaymentFilter
  transfersFilters?: BankingTransferFilter
}

const prepareData = (data: PaymentsOverdueWidgetQuery, prepareDataArgs: PreparedDataArgsType) => {
  const overdueAmount =
    (data?.banking?.overdue?.aggregate?.sum?.amountCents || 0) +
    (data?.banking.overdueTransfers.aggregate?.sum?.amountCents || 0)
  const dueThisWeekAmount =
    (data?.banking?.dueThisWeek?.aggregate?.sum?.amountCents || 0) +
    (data?.banking.dueThisWeekTransfers.aggregate?.sum?.amountCents || 0)
  const onTimeAmount =
    (data?.banking?.onTime?.aggregate?.sum?.amountCents || 0) +
    (data?.banking.onTimeTransfers.aggregate?.sum?.amountCents || 0)
  const overdueCount =
    (data?.banking?.overdue?.aggregate?.count || 0) +
    (data?.banking.overdueTransfers.aggregate?.count || 0)
  const dueThisWeekCount =
    (data?.banking?.dueThisWeek?.aggregate?.count || 0) +
    (data?.banking.dueThisWeekTransfers.aggregate?.count || 0)
  const onTimeCount =
    (data?.banking?.onTime?.aggregate?.count || 0) +
    (data?.banking.onTimeTransfers.aggregate?.count || 0)
  const totalAmount = overdueAmount + dueThisWeekAmount + onTimeAmount
  const totalCount = overdueCount + dueThisWeekCount + onTimeCount

  const overviewChartData: ChartData = {
    label: `${i18n.t('finance.overview.payments.total')} (${totalCount})`,
    value: totalAmount,
    formattedValue: prepareDataArgs.amountFormatter(totalAmount),
  }

  const chartDatas: ChartData[] = [
    {
      status: 'onTime',
      color: 'categorical-2',
      label: `${i18n.t('finance.overview.payments.on_time')} (${onTimeCount})`,
      value: onTimeAmount,
      formattedValue: prepareDataArgs.amountFormatter(onTimeAmount),
    },
    {
      status: 'dueThisWeek',
      color: 'categorical-3',
      label: `${i18n.t('finance.overview.payments.due_this_week')} (${dueThisWeekCount})`,
      value: dueThisWeekAmount,
      formattedValue: prepareDataArgs.amountFormatter(dueThisWeekAmount),
    },
    {
      status: 'overdue',
      color: 'feedback-negative',
      label: `${i18n.t('finance.overview.payments.overdue')} (${overdueCount})`,
      value: overdueAmount,
      formattedValue: prepareDataArgs.amountFormatter(overdueAmount),
    },
  ]

  const transfersOnly = (() => {
    const paymentsCount =
      (data?.banking?.overdue?.aggregate?.count || 0) +
      (data?.banking?.dueThisWeek?.aggregate?.count || 0) +
      (data?.banking?.onTime?.aggregate?.count || 0)
    const transfersCount =
      (data?.banking.overdueTransfers.aggregate?.count || 0) +
      (data?.banking.dueThisWeekTransfers.aggregate?.count || 0) +
      (data?.banking.onTimeTransfers.aggregate?.count || 0)

    return !paymentsCount && !!transfersCount
  })()

  return {
    transfersOnly,
    overviewChartData,
    chartDatas,
    amountFormatter: prepareDataArgs.amountFormatter,
    displayedFilters: prepareDataArgs.displayedFilters,
  }
}

export const buildPaymentsOverdueDashboardWidget = ({
  queryArgs,
  prepareDataArgs,
}: {
  queryArgs: QueryArgsType
  prepareDataArgs: PreparedDataArgsType
}) => {
  const PaymentsOverdueDashboardWidget: FC<{
    data: ReturnType<typeof prepareData>
  }> = ({ data }) => {
    const { navigation } = useNavigation()
    const { overviewChartData, chartDatas, amountFormatter, displayedFilters, transfersOnly } = data

    const { paymentsFilters } = queryArgs

    const [from, to] = paymentsFilters?.date?.between || []

    let dateQueryString = ''
    if (from && to) {
      dateQueryString = `createdAtFrom=${from}&createdAtTo=${to}`
    }

    const baseRoute = !transfersOnly
      ? navigation.finance.banking.payments.fullPath()
      : navigation.finance.banking.payments.transfers.fullPath()

    return (
      <FinanceWidget
        id="paymentsOverdueDashboard"
        title={i18n.t('finance.overview.payments.payments_by_overdue')}
        displayedFilters={displayedFilters}
        tickFormatter={(value) => amountFormatter(Number(value))}
        type={SupportedCharts.PIE}
        showChart
        chartDatas={chartDatas}
        overviewChartData={overviewChartData}
        linkData={{
          url: `${baseRoute}?${dateQueryString}`,
          title: i18n.t('banking.payments.short_title'),
        }}
        emptyState={{
          icon: '💰',
          text: i18n.t('finance.overview.payments.empty_state_by_due_date.title'),
          description: i18n.t('finance.overview.payments.empty_state_by_due_date.description'),
        }}
      />
    )
  }

  return buildWidget({
    id: 'paymentsOverdueWidget',
    component: PaymentsOverdueDashboardWidget,
    document: PaymentsOverdueWidgetDocument,
    prepareData: (data: PaymentsOverdueWidgetQuery) => prepareData(data, prepareDataArgs),
    queryArgs: {
      ...queryArgs,
    },
  })
}
